import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/api/client";
import { useStore, usePhase } from "@/state/store";
import { useWinningTopic } from "@/hooks/useWinningTopic";
import { OutputRenderer } from "@/workshop/OutputRenderer";
import type {
  Evaluation,
  Output,
  RequirementFields,
  Team,
  TeamRequirement,
} from "@offsite/shared";

// Showcase ist erst in der Reveal-Phase für alle freigeschaltet (auch
// für den Presenter über den Sidebar-Link). Vorher: Hinweis-Bildschirm.
const SHOWCASE_PHASES = new Set<string>(["reveal"]);

interface ShowcaseEntry {
  teamNumber: number;
  teamName: string | null;
  requirement: RequirementFields | null;
  evaluation: Evaluation | null;
  output: Output | null;
}

const FIELD_LABELS: { key: keyof RequirementFields; label: string }[] = [
  { key: "was", label: "Was soll gebaut werden?" },
  { key: "fuerWen", label: "Für wen?" },
  { key: "problem", label: "Welches Problem?" },
  { key: "anforderungen", label: "Was muss es können?" },
  { key: "einschraenkungen", label: "Was darf es NICHT?" },
  { key: "beispiel", label: "Beispiel-Szenario" },
];

export function ShowcasePage() {
  const { state } = useStore();
  const token = state.auth.token;
  const phase = usePhase();
  const isPresenter = state.auth.role === "presenter";
  const allowed = SHOWCASE_PHASES.has(phase) || isPresenter;
  const winner = useWinningTopic();
  const [entries, setEntries] = useState<ShowcaseEntry[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [section, setSection] = useState<"req" | "eval" | "output">("output");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [teamsR, evalsR, outsR] = await Promise.all([
          api.teams.list(token),
          api.evaluations.list(token),
          api.overnight.outputs(token),
        ]);
        if (cancelled) return;
        const teams: Team[] = teamsR.teams;
        const evalByN = new Map(evalsR.evaluations.map((e) => [e.teamNumber, e]));
        const outByN = new Map(outsR.outputs.map((o) => [o.teamNumber, o]));

        const reqs = await Promise.all(
          teams.map(async (t) => {
            try {
              const r = await api.teams.getReq(token, t.number);
              return [t.number, r.requirement] as [number, TeamRequirement];
            } catch (err) {
              if (err instanceof ApiError && err.status === 404) return [t.number, null] as [number, null];
              throw err;
            }
          }),
        );
        if (cancelled) return;
        const reqByN = new Map(reqs);

        const next: ShowcaseEntry[] = teams
          .sort((a, b) => a.number - b.number)
          .map((t) => ({
            teamNumber: t.number,
            teamName: t.name,
            requirement: reqByN.get(t.number)?.fields ?? null,
            evaluation: evalByN.get(t.number) ?? null,
            output: outByN.get(t.number) ?? null,
          }));
        setEntries(next);
        setLoading(false);
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  const active = entries[activeIdx];

  const goPrev = () => setActiveIdx((i) => (i - 1 + entries.length) % entries.length);
  const goNext = () => setActiveIdx((i) => (i + 1) % entries.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length]);

  // Beacon: Showcase ist Light-Skin (Brief 04 #17).
  useEffect(() => {
    document.documentElement.classList.add("skin-light");
    document.documentElement.classList.remove("skin-presenter");
  }, []);

  // Default to output tab if available, else evaluation, else requirement.
  useEffect(() => {
    if (!active) return;
    if (active.output) setSection("output");
    else if (active.evaluation) setSection("eval");
    else if (active.requirement) setSection("req");
  }, [active, activeIdx]);

  const tabs = useMemo(
    () => [
      { id: "req" as const, label: "Anforderung", available: !!active?.requirement },
      { id: "eval" as const, label: "Bewertung", available: !!active?.evaluation },
      { id: "output" as const, label: "Ergebnis", available: !!active?.output },
    ],
    [active],
  );

  if (!allowed) {
    return (
      <main className="showcase-shell">
        <div className="showcase-empty">
          <h2>Noch nicht freigegeben</h2>
          <p>
            Die Showcase-Seite wird am Ende des Workshops freigeschaltet —
            sobald die KI-Outputs für alle Teams stehen.
          </p>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="showcase-shell">
        <div className="showcase-empty">Lade Workshop-Ergebnisse …</div>
      </main>
    );
  }

  if (entries.length === 0) {
    return (
      <main className="showcase-shell">
        <div className="showcase-empty">
          <h2>Noch keine Teams</h2>
          <p>Sobald die Teams verteilt sind, erscheinen hier die Ergebnisse.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="showcase-shell">
      <div className="deck-bg" aria-hidden="true" />

      <header className="showcase-head">
        <div className="deck-eyebrow" style={{ color: "var(--svo-blau)" }}>
          Workshop-Ergebnisse
        </div>
        {winner && (
          <div className="showcase-topic">
            Thema: <strong>{winner.topic.title}</strong>
          </div>
        )}
        <h1 className="deck-title showcase-title">
          Was unsere <span className="accent">Teams</span> gebaut haben
        </h1>
      </header>

      {/* Carousel — chip strip with arrows */}
      <div className="showcase-carousel">
        <button
          type="button"
          className="showcase-arrow"
          onClick={goPrev}
          disabled={entries.length < 2}
          aria-label="Vorheriges Team"
        >
          ‹
        </button>
        <div className="showcase-cards">
          {entries.map((e, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={e.teamNumber}
                type="button"
                className={`showcase-card${isActive ? " active" : ""}`}
                onClick={() => setActiveIdx(i)}
              >
                <div className="num">{e.teamNumber}</div>
                <div className="meta">
                  <h4>{e.teamName ?? `Team ${e.teamNumber}`}</h4>
                  {e.evaluation && (
                    <div className="score">
                      {e.evaluation.score.toFixed(1)}
                      <span className="max">/10</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="showcase-arrow"
          onClick={goNext}
          disabled={entries.length < 2}
          aria-label="Nächstes Team"
        >
          ›
        </button>
      </div>

      {/* Detail panel */}
      {active && (
        <article className="showcase-detail">
          <header className="showcase-detail-head">
            <div>
              <div className="eyebrow">Team {active.teamNumber}</div>
              <h2>{active.teamName ?? `Team ${active.teamNumber}`}</h2>
            </div>
            {active.evaluation && (
              <div className="showcase-score-pill">
                <strong>{active.evaluation.score.toFixed(1)}</strong>
                <small>/10</small>
              </div>
            )}
          </header>

          <div className="showcase-tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`showcase-tab${section === t.id ? " active" : ""}`}
                onClick={() => t.available && setSection(t.id)}
                disabled={!t.available}
              >
                {t.label}
                {!t.available && <span className="missing"> · —</span>}
              </button>
            ))}
          </div>

          <div className="showcase-section">
            {section === "req" && active.requirement && (
              <div className="showcase-req-grid">
                {FIELD_LABELS.map(({ key, label }) => {
                  const v = active.requirement![key];
                  return (
                    <div key={key} className="showcase-req-field">
                      <div className="showcase-req-label">{label}</div>
                      <div className="showcase-req-value">{v || "—"}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {section === "eval" && active.evaluation && (
              <div className="showcase-eval">
                <blockquote className="showcase-eval-quote">
                  „{active.evaluation.zusammenfassung}"
                </blockquote>
                <div className="showcase-eval-cards">
                  <div className="showcase-eval-card is-strengths">
                    <h4>✓ Was ist gut</h4>
                    <ul>
                      {active.evaluation.gut.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="showcase-eval-card is-improvements">
                    <h4>△ Was ist ausbaufähig</h4>
                    <ul>
                      {active.evaluation.ausbaufaehig.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <footer className="showcase-eval-footer">
                  <code>{active.evaluation.modelId}</code> ·{" "}
                  {(active.evaluation.durationMs / 1000).toFixed(1)}s ·{" "}
                  {active.evaluation.inputTokens}/{active.evaluation.outputTokens} Tokens
                </footer>
              </div>
            )}

            {section === "output" && active.output && (
              <div className="showcase-output">
                <OutputRenderer output={active.output} />
                <footer className="showcase-eval-footer">
                  <code>{active.output.modelId}</code> ·{" "}
                  {(active.output.durationMs / 1000).toFixed(1)}s ·{" "}
                  {active.output.inputTokens}/{active.output.outputTokens} Tokens · Format:{" "}
                  <code>{active.output.contentType}</code>
                </footer>
              </div>
            )}
          </div>
        </article>
      )}

      <footer className="showcase-foot">
        ← / → · {activeIdx + 1} / {entries.length}
      </footer>
    </main>
  );
}
